// ----------------------------------------------------------------------

import { settings } from "nprogress";

function path(root, sublink) {
  return `${root}${sublink}`;
}

// const ROOT_HOME = '/jj';
const ROOTS_AUTH = '/auth';
const ROOTS_DASHBOARD = '/dashboard';
const ROOTS_STUDENT = '/student';
const ROOTS_UNIVERSITY = '/university';
const ROOTS_COMPANY = '/company';

const ROOTS_ADMIN = '/admin';
const ROOTS_KIOSK = '/cik';
const ROOTS_USER = '/user';
const ROOTS_RESTAURANT_ADMIN = '/restaurant-admin';
const ROOTS_ATLAS = '/atlas';
const ROOTS_LIVE_INVENTORY = '/live-inventory';
const ROOTS_GET_STARTED = '/get-started';

// ----------------------------------------------------------------------
export const PATH_AUTH = {
  root: ROOTS_AUTH,
  login: path(ROOTS_AUTH, '/login'),
  register: path(ROOTS_AUTH, '/register'),
  signUp: path(ROOTS_AUTH, '/signup'),
  verifyOtp: path(ROOTS_AUTH, '/verify-otp'),
  loginUnprotected: path(ROOTS_AUTH, '/login-unprotected'),
  registerUnprotected: path(ROOTS_AUTH, '/register-unprotected'),
  verify: path(ROOTS_AUTH, '/verify'),
  resetPassword: path(ROOTS_AUTH, '/reset-password'),
  newPassword: path(ROOTS_AUTH, '/new-password'),
  crossing: path(ROOTS_AUTH, '/crossing'),
};

// Self-serve signup for coworking/office-space admins
export const PATH_SIGNUP = {
  root: ROOTS_GET_STARTED,
  onboarding: path(ROOTS_GET_STARTED, '/onboarding'),
};

export const PATH_ADMIN = {
  root: ROOTS_ADMIN,
  login: path(ROOTS_ADMIN, '/login'),
  dashboard: path(ROOTS_ADMIN, '/dashboard'),
  documentation: path(ROOTS_ADMIN, '/documentation'),
  // API keys and the access log. Reached from the account menu, never the nav.
  developer: path(ROOTS_ADMIN, '/developer'),
  developerKey: (id) => path(ROOTS_ADMIN, `/developer/keys/${id}`),
  // A guided tour as a link, for Dex answers, emails and support replies.
  guide: (id) => path(ROOTS_ADMIN, `/guides/${id}`),
  profile: path(ROOTS_ADMIN, '/profile'),
  SpaceManagement: path(ROOTS_ADMIN, '/space-management'),
  addSpace: path(ROOTS_ADMIN, '/space-management/add'),
  // Trailing slash matches the route definition, which the guide engine compares
  // against the live pathname character for character.
  floorPlans: path(ROOTS_ADMIN, '/space-management/floor-plans/list/'),
  teamMembers: path(ROOTS_ADMIN, '/org/member'),
  editSpace: (id) => path(ROOTS_ADMIN, `/space-management/edit/${id}`),
  monthlyPlanlist: path(ROOTS_ADMIN, '/monthly-plans'),
  createMonthlyPlan: path(ROOTS_ADMIN, '/create-monthly-plan'),
  doorManagement: path(ROOTS_ADMIN, '/access-control/doors'),
  generalPlans: path(ROOTS_ADMIN, '/plans-and-amenities'),
  // The one plans page. The three routes above now redirect here.
  plans: {
    root: path(ROOTS_ADMIN, '/plans'),
    new: (type) => path(ROOTS_ADMIN, type ? `/plans/new?type=${encodeURIComponent(type)}` : '/plans/new'),
    edit: (kind, id) => path(ROOTS_ADMIN, `/plans/${kind}/${id}`),
  },
  visitPlans: {
    root: path(ROOTS_ADMIN, '/plan-management'),
    add: path(ROOTS_ADMIN, '/plan-management/add'),
    edit: (id) => path(ROOTS_ADMIN, `/plan-management/edit/${id}`),
  },
  liveStatus: path(ROOTS_ADMIN, '/space/live-control'),
  liveControlCenter: path(ROOTS_ADMIN, '/space/live-control'),
  checkIns: path(ROOTS_ADMIN, '/check-ins'),
  mealosAdmin: {
    root: path(ROOTS_ADMIN, '/mealos/dashboard'),
    cafeteriaManagement: path(ROOTS_ADMIN, '/mealos/cafeteria'),
    staffAssignment: path(ROOTS_ADMIN, '/mealos/staff-assignment'),
  },
  shopAdmin: {
    root: path(ROOTS_ADMIN, '/shop'),
  },
  inspectAdmin: {
    root: path(ROOTS_ADMIN, '/inspect'),
    overview: path(ROOTS_ADMIN, '/inspect/overview'),
    // Dashboard and Analytics were two pages that overlapped and disagreed.
    // Both paths still resolve, to Overview, so old links keep working.
    dashboard: path(ROOTS_ADMIN, '/inspect/dashboard'),
    // Sessions became Records: the page stopped being a log to browse and
    // became the place a month gets reconstructed. The old path still resolves
    // to it so links already sent to somebody keep working.
    records: path(ROOTS_ADMIN, '/inspect/records'),
    sessions: path(ROOTS_ADMIN, '/inspect/sessions'),
    issues: path(ROOTS_ADMIN, '/inspect/issues'),
    analytics: path(ROOTS_ADMIN, '/inspect/analytics'),
    // Templates became Setup: the tab strip stopped being a list of
    // checklists and became the whole of how a campus is configured. The
    // old path still resolves to it so old links keep working.
    setup: path(ROOTS_ADMIN, '/inspect/setup'),
    templates: path(ROOTS_ADMIN, '/inspect/templates'),
    electricity: path(ROOTS_ADMIN, '/inspect/electricity'),
  },
  members: {
    root: path(ROOTS_ADMIN, '/memberships/members'),
    summary: path(ROOTS_ADMIN, '/memberships/summary'),
    add: path(ROOTS_ADMIN, '/memberships/members/add'),
    subscriptions: path(ROOTS_ADMIN, '/memberships/subscriptions'),
    // One membership, its own page. The company page above lists them; this is
    // the one that opens when you pick one.
    membership: (subscriptionId) => path(ROOTS_ADMIN, `/memberships/membership/${subscriptionId}`),
    autoRenewals: path(ROOTS_ADMIN, '/memberships/subscriptions/auto-renewals'),
    visitPlans: path(ROOTS_ADMIN, '/memberships/visit-plans'),
    membershipsPage: path(ROOTS_ADMIN, '/memberships/home'),
  },
  inbox: {
    root: path(ROOTS_ADMIN, '/inbox'),
    approvals: path(ROOTS_ADMIN, '/inbox?tab=approvals'),
    exceptions: path(ROOTS_ADMIN, '/inbox?tab=exceptions'),
  },
  // Kept so old links and bookmarks still resolve. Both now redirect into the
  // Inbox, which is where approvals and exceptions live.
  exceptions: {
    root: path(ROOTS_ADMIN, '/exceptions'),
  },
  // Staff attendance and payroll for the operator's OWN employees. Deliberately
  // not under `settings`: the register is a daily operational screen, and only
  // the configuration part of it belongs in Settings.
  workforce: {
    root: path(ROOTS_ADMIN, '/workforce'),
    register: path(ROOTS_ADMIN, '/workforce/attendance'),
    muster: path(ROOTS_ADMIN, '/workforce/muster'),
    staff: path(ROOTS_ADMIN, '/workforce/staff'),
    staffDetail: (id) => path(ROOTS_ADMIN, `/workforce/staff/${id}`),
    leave: path(ROOTS_ADMIN, '/workforce/leave'),
    requests: path(ROOTS_ADMIN, '/workforce/requests'),
    roster: path(ROOTS_ADMIN, '/workforce/roster'),
    payroll: path(ROOTS_ADMIN, '/workforce/payroll'),
    payrollRun: (id) => path(ROOTS_ADMIN, `/workforce/payroll/${id}`),
    payrollSettings: path(ROOTS_ADMIN, '/workforce/payroll/settings'),
    advances: path(ROOTS_ADMIN, '/workforce/advances'),
    reports: path(ROOTS_ADMIN, '/workforce/reports'),
    me: path(ROOTS_ADMIN, '/workforce/my-attendance'),
    myTeam: path(ROOTS_ADMIN, '/workforce/my-team'),
    myPay: path(ROOTS_ADMIN, '/workforce/my-pay'),
    settings: path(ROOTS_ADMIN, '/workforce/settings'),
    setup: path(ROOTS_ADMIN, '/workforce/setup'),
  },
  orderManagement: {
    root: path(ROOTS_ADMIN, '/order-management'),
    invoiceList: path(ROOTS_ADMIN, '/order-management/invoice/list'),
    list: path(ROOTS_ADMIN, '/order-management/list'),
    create: path(ROOTS_ADMIN, '/order-management/create'),
    createV2: path(ROOTS_ADMIN, '/order-management/create-v2'),
    createPlan: path(ROOTS_ADMIN, '/order-management/create-plan'),
    billingHandoffs: path(ROOTS_ADMIN, '/order-management/billing-handoffs'),
    paymentManagement: path(ROOTS_ADMIN, '/order-management/payment-management'),
    receiptList: path(ROOTS_ADMIN, '/order-management/receipt/list'),
    creditNoteList: path(ROOTS_ADMIN, '/order-management/credit-notes'),
    createCreditNote: path(ROOTS_ADMIN, '/order-management/credit-notes/create'),
    collectionRegister: path(ROOTS_ADMIN, '/order-management/collection-register'),
    billToAccount: path(ROOTS_ADMIN, '/order-management/bill-to-account'),
    invoiceNumbers: path(ROOTS_ADMIN, '/order-management/invoice-numbers'),
  },
  sync:{
    root: path(ROOTS_ADMIN, '/sync'),
    syncHome: path(ROOTS_ADMIN, '/sync/home'),
    syncTally: path(ROOTS_ADMIN, '/sync/tally'),
    syncTallyMapping: path(ROOTS_ADMIN, '/sync/tally/mapping'),
  },
  leaseManagement: {
    root: path(ROOTS_ADMIN, '/lease-management'),
    home: path(ROOTS_ADMIN, '/lease-management/home'),
    leases: path(ROOTS_ADMIN, '/lease-management/leases'),
    escalations: path(ROOTS_ADMIN, '/lease-management/escalations'),
    leaseDetail: (id) => path(ROOTS_ADMIN, `/lease-management/lease/${id}`),
    deposits: path(ROOTS_ADMIN, '/lease-management/deposits'),
    templateBuilder: path(ROOTS_ADMIN, '/lease-management/template-builder'),
  },
  virtualOffice: {
    root: path(ROOTS_ADMIN, '/virtual-office'),
    list: path(ROOTS_ADMIN, '/virtual-office/list'),
    create: path(ROOTS_ADMIN, '/virtual-office/create'),
    detail: (id) => path(ROOTS_ADMIN, `/virtual-office/${id}`),
    edit: (id) => path(ROOTS_ADMIN, `/virtual-office/${id}/edit`),
    amenityPackages: path(ROOTS_ADMIN, '/virtual-office/amenity-packages'),
    templateBuilder: path(ROOTS_ADMIN, '/virtual-office/template-builder'),
  },
  meetingRoomManagement: {
    root: path(ROOTS_ADMIN, '/meeting-room-management'),
    calendar: (id) => path(ROOTS_ADMIN, `/meeting-room-management/calendar/${id}`),
    resourceTypes: path(ROOTS_ADMIN, '/meeting-room-management/resource-types'),
  },
  createMeetingRoom: {
    root: path(ROOTS_ADMIN, '/create/meeting-room'),
  },
  // The old inventoryManagement paths are gone. Inventory is Consumables
  // inside Expenses now, and /admin/inventory-management/* only survives as
  // redirects in routes/index.js for old bookmarks.
  expenses: {
    root: path(ROOTS_ADMIN, '/expenses'),
    dashboard: path(ROOTS_ADMIN, '/expenses/dashboard'),
    all: path(ROOTS_ADMIN, '/expenses/all'),
    approvals: path(ROOTS_ADMIN, '/expenses/approvals'),
    detail: (id) => path(ROOTS_ADMIN, `/expenses/${id}`),
    vendors: path(ROOTS_ADMIN, '/expenses/vendors'),
    budgets: path(ROOTS_ADMIN, '/expenses/budgets'),
    recurring: path(ROOTS_ADMIN, '/expenses/recurring'),
    pettyCash: path(ROOTS_ADMIN, '/expenses/petty-cash'),
    reports: path(ROOTS_ADMIN, '/expenses/reports'),
    settings: path(ROOTS_ADMIN, '/expenses/settings'),
    settingsCategories: path(ROOTS_ADMIN, '/expenses/settings/categories'),
    settingsApprovalChains: path(ROOTS_ADMIN, '/expenses/settings/approval-chains'),
    settingsAccess: path(ROOTS_ADMIN, '/expenses/settings/access'),
    settingsCapacity: path(ROOTS_ADMIN, '/expenses/settings/capacity'),
    consumables: path(ROOTS_ADMIN, '/expenses/consumables'),
    consumablesInventory: path(ROOTS_ADMIN, '/expenses/consumables/inventory'),
    consumablesPurchases: path(ROOTS_ADMIN, '/expenses/consumables/purchases'),
    consumablesConsumption: path(ROOTS_ADMIN, '/expenses/consumables/consumption'),
  },
  orgMember: {
    root: path(ROOTS_ADMIN, '/org/member'),
  },
  settlements: {
    root: path(ROOTS_ADMIN, '/settlements'),
  },
  deskosBilling: {
    root: path(ROOTS_ADMIN, '/deskos-billing'),
  },
  deskosSupport: {
    root: path(ROOTS_ADMIN, '/deskos-support'),
  },
  deskosTraining: {
    root: path(ROOTS_ADMIN, '/deskos-training'),
  },
  printing: {
    root: path(ROOTS_ADMIN, '/printing'),
  },
  serviceRequests: {
    home: path(ROOTS_ADMIN, '/service-requests/home'),
    tickets: path(ROOTS_ADMIN, '/service-requests/tickets'),
    view: (id) => path(ROOTS_ADMIN, `/service-requests/view/${id}`),
    create: path(ROOTS_ADMIN, '/service-requests/create'),
    analytics: path(ROOTS_ADMIN, '/service-requests/analytics'),
    category: path(ROOTS_ADMIN, '/service-requests/settings/category'),
    teams: path(ROOTS_ADMIN, '/service-requests/settings/team'),
    // Work orders are staff tasks. They share the helpdesk table and this
    // section of the nav, and they are never shown to a member.
    workOrders: path(ROOTS_ADMIN, '/service-requests/work-orders'),
    workOrder: (id) => path(ROOTS_ADMIN, `/service-requests/work-orders/${id}`),
    // The reusable task definitions everything else points at. Referenced by
    // the automations catalogue as the place to go when a rule has no
    // template to raise, so this path is part of a contract.
    workOrderTemplates: path(ROOTS_ADMIN, '/service-requests/work-order-templates'),
  },
  accounting: {
    root: path(ROOTS_ADMIN, '/accounting'),
    billingManagement: path(ROOTS_ADMIN, '/accounting/billing-management'),
    invoiceManagement: path(ROOTS_ADMIN, '/accounting/invoice-management'),
    gstManagement : path(ROOTS_ADMIN, '/accounting/gst-management'),
  },
  financials: {
    invoices: path(ROOTS_ADMIN, '/financials/invoices'),
    receivables: path(ROOTS_ADMIN, '/financials/receivables'),
    pendingBillables: path(ROOTS_ADMIN, '/financials/pending-billables'),
    ledger: path(ROOTS_ADMIN, '/financials/ledger'),
    creditDebitNotes: path(ROOTS_ADMIN, '/financials/credit-debit-notes'),
  },
  reports: {
    root: path(ROOTS_ADMIN, '/reports'),
  },
  analytics: {
    root: path(ROOTS_ADMIN, '/analytics'),
    builder: path(ROOTS_ADMIN, '/analytics/builder'),
    report: (code) => path(ROOTS_ADMIN, `/analytics/report/${code}`),
    dashboard: (id) => path(ROOTS_ADMIN, `/analytics/dashboard/${id}`),
    templateDashboard: (code) => path(ROOTS_ADMIN, `/analytics/dashboard/template/${code}`),
  },
  settings: {
    root: path(ROOTS_ADMIN, '/settings'),
    // Settings is one page with a tab selector, driven by the query string.
    // This is how a guide, a Dex answer or a CTA links straight to one setting.
    // It has to build on /settings/general, which is where that page is
    // actually mounted. `root` below is only the nav group and has no route of
    // its own, so linking to it lands on the 404 page.
    tab: (section, child) => path(ROOTS_ADMIN, `/settings/general?section=${section}&child=${child}`),
    general: path(ROOTS_ADMIN, '/settings/general'),
    integrations: path(ROOTS_ADMIN, '/settings/integrations'),
    tallyConfig: path(ROOTS_ADMIN, '/settings/integrations/tally/configure'),
    tallyMappings: path(ROOTS_ADMIN, '/settings/integrations/tally/mappings'),
    tallyConnect: path(ROOTS_ADMIN, '/settings/integrations/tally/connect'),
    accountingSync: path(ROOTS_ADMIN, '/settings/integrations/accounting'),
    // Cloud telephony. Its own page rather than the generic integration dialog: the
    // credentials live on their own model, and the number-to-campus mapping the
    // integration rests on cannot be expressed in a two-field dialog.
    knowlarity: path(ROOTS_ADMIN, '/settings/integrations/knowlarity'),
    nexudus: path(ROOTS_ADMIN, '/settings/integrations/nexudus'),
    // Bring-your-own MSG91: the org's own MSG91 account and its own approved templates.
    whatsappMsg91: path(ROOTS_ADMIN, '/settings/integrations/whatsapp'),
    // The org's own WhatsApp number, connected direct to Meta. Separate from
    // /settings/integrations, which is the bring-your-own-vendor surface.
    whatsapp: path(ROOTS_ADMIN, '/settings/whatsapp'),
    aiCredits: path(ROOTS_ADMIN, '/settings/ai-credits'),
    aiConnections: path(ROOTS_ADMIN, '/settings/ai-connections'),
    imports: path(ROOTS_ADMIN, '/settings/imports'),
    space_import: path(ROOTS_ADMIN, '/settings/imports/space'),
    lease_import: path(ROOTS_ADMIN, '/settings/imports/lease'),
    lease_import_validate: path(ROOTS_ADMIN, '/settings/imports/lease/validate'),
    billing_import: path(ROOTS_ADMIN, '/settings/imports/billing-address'),
    roles: path(ROOTS_ADMIN, '/settings/roles'),
    collectionAccounts: path(ROOTS_ADMIN, '/settings/collection-accounts'),
  },
  coupons: path(ROOTS_ADMIN, '/coupons'),
  community: path(ROOTS_ADMIN, '/community'),
  events: {
    root: path(ROOTS_ADMIN, '/events'),
    create: path(ROOTS_ADMIN, '/events/create'),
    edit: (id) => path(ROOTS_ADMIN, `/events/edit/${id}`),
    detail: (id) => path(ROOTS_ADMIN, `/events/${id}`),
  },
  downloads: path(ROOTS_ADMIN, '/downloads'),
  visitorManagement: path(ROOTS_ADMIN, '/visitor-management'),
  courier: path(ROOTS_ADMIN, '/courier'),
  courierSettings: path(ROOTS_ADMIN, '/courier/settings'),
  signage: path(ROOTS_ADMIN, '/signage'),
  leadsManagement: {
    root: path(ROOTS_ADMIN, '/leads-management'),
    reports: path(ROOTS_ADMIN, '/leads-management/reports'),
    dashboard: path(ROOTS_ADMIN, '/leads-management/dashboard'),
    myDay: path(ROOTS_ADMIN, '/leads-management/my-day'),
    team: path(ROOTS_ADMIN, '/leads-management/team'),
    teamMemberDay: (id) => path(ROOTS_ADMIN, `/leads-management/team/${id}`),
    list: path(ROOTS_ADMIN, '/leads-management/leads'),
    leadDetail: (id) => path(ROOTS_ADMIN, `/leads-management/leads/${id}`),
    followUps: path(ROOTS_ADMIN, '/leads-management/follow-ups'),
    invitations: path(ROOTS_ADMIN, '/leads-management/invitations'),
    deals: path(ROOTS_ADMIN, '/leads-management/deals'),
    proposals: path(ROOTS_ADMIN, '/leads-management/proposals'),
    createProposal: path(ROOTS_ADMIN, '/leads-management/proposal/create'),
    proposalSettings: path(ROOTS_ADMIN, '/leads-management/proposal-settings'),
    proposalPDFTemplate: path(ROOTS_ADMIN, '/leads-management/proposal-pdf-template'),
    proposalTemplates: path(ROOTS_ADMIN, '/leads-management/proposal-templates'),
    proposalTemplateBuilder: (id) =>
      path(ROOTS_ADMIN, `/leads-management/proposal-templates/${id}`),
    settings: path(ROOTS_ADMIN, '/leads-management/settings'),
    forms: path(ROOTS_ADMIN, '/leads-management/forms'),
    website: path(ROOTS_ADMIN, '/leads-management/website'),
    websiteTab: (tab) => path(ROOTS_ADMIN, `/leads-management/website?tab=${tab}`),
    formBuilder: (id) => path(ROOTS_ADMIN, `/leads-management/forms/${id}`),
    // legacy redirects
    home: path(ROOTS_ADMIN, '/leads-management/dashboard'),
  },
  dex: path(ROOTS_ADMIN, '/dex'),
  communication: path(ROOTS_ADMIN, '/communication'),
  accessControl: {
    root: path(ROOTS_ADMIN, '/access-control'),
    users: path(ROOTS_ADMIN, '/access-control/users'),
    cards: path(ROOTS_ADMIN, '/access-control/cards'),
    zkbioSetup: path(ROOTS_ADMIN, '/access-control/zkbio-setup'),
    accessSetup: path(ROOTS_ADMIN, '/access-control/zkbio-setup'),
    kisiSetup: path(ROOTS_ADMIN, '/access-control/kisi-setup'),
    spintlySaamsSetup: path(ROOTS_ADMIN, '/access-control/spintly-saams-setup'),
    codeUnlockSetup: path(ROOTS_ADMIN, '/access-control/code-unlock-setup'),
    eventLogs: path(ROOTS_ADMIN, '/access-control/event-logs'),
    insights: path(ROOTS_ADMIN, '/access-control/insights'),
  },
  // Member WiFi: who is on it and which bandwidth group they are in. Works
  // against whatever portal the campus has connected, and names no vendor.
  network: {
    root: path(ROOTS_ADMIN, '/network'),
    // Connecting a portal is an integration, so setup lives under Settings ->
    // Integrations. /admin/network is only for managing members against
    // whatever is connected there.
    setup: (slug) => path(ROOTS_ADMIN, `/settings/integrations/captive-portal/${slug}`),
  },
  parkingManagement: {
    root: path(ROOTS_ADMIN, '/parking'),
  },
  assetManagement: {
    root: path(ROOTS_ADMIN, '/asset-management'),
    settings: path(ROOTS_ADMIN, '/asset-management/settings'),
    detail: (id) => path(ROOTS_ADMIN, `/asset-management/assets/${id}`),
    maintenance: path(ROOTS_ADMIN, '/asset-management/maintenance'),
    amc: path(ROOTS_ADMIN, '/asset-management/amc'),
    insurance: path(ROOTS_ADMIN, '/asset-management/insurance'),
    vendors: path(ROOTS_ADMIN, '/asset-management/vendors'),
    floorMap: path(ROOTS_ADMIN, '/asset-management/floor-map'),
    analytics: path(ROOTS_ADMIN, '/asset-management/analytics'),
    damage: path(ROOTS_ADMIN, '/asset-management/damage'),
    assignments: path(ROOTS_ADMIN, '/asset-management/assignments'),
    audits: path(ROOTS_ADMIN, '/asset-management/audits'),
    auditRun: (id) => path(ROOTS_ADMIN, `/asset-management/audits/${id}`),
    scan: path(ROOTS_ADMIN, '/asset-management/scan'),
  },
  meetingRoomDisplays: path(ROOTS_ADMIN, '/settings/meeting-room-displays'),
  meetingRoomCreditPlans: path(ROOTS_ADMIN, '/plans?tab=credit-packs'),
  automations: path(ROOTS_ADMIN, '/automations'),
  automationBuilder: path(ROOTS_ADMIN, '/automations/builder'),
  automationEventBuilder: (id) => path(ROOTS_ADMIN, `/automations/builder/event/${id}`),
  automationRuns: path(ROOTS_ADMIN, '/automations/runs'),
  automationRules: path(ROOTS_ADMIN, '/automations/rules'),
  chatbots: {
    root: path(ROOTS_ADMIN, '/chatbots'),
    dashboard: path(ROOTS_ADMIN, '/chatbots/dashboard'),
    builder: (id) => path(ROOTS_ADMIN, `/chatbots/${id}/builder`),
    // The AI bot's own page: everything about one bot, tabbed. `settings` above
    // is still the list of bots.
    bot: (id) => path(ROOTS_ADMIN, `/chatbots/${id}`),
    inbox: path(ROOTS_ADMIN, '/chatbots/inbox'),
    settings: path(ROOTS_ADMIN, '/chatbots/settings'),
    knowledge: path(ROOTS_ADMIN, '/chatbots/knowledge'),
  },
  chatWidget: (botId) => `/chat/${botId}`,
  outreach: {
    root: path(ROOTS_ADMIN, '/outreach'),
    dashboard: path(ROOTS_ADMIN, '/outreach/dashboard'),
    campaigns: path(ROOTS_ADMIN, '/outreach/campaigns'),
    newCampaign: path(ROOTS_ADMIN, '/outreach/campaigns/new'),
    newAiCampaign: path(ROOTS_ADMIN, '/outreach/campaigns/new/ai'),
    editCampaign: (id) => path(ROOTS_ADMIN, `/outreach/campaigns/${id}/edit`),
    campaignDetail: (id) => path(ROOTS_ADMIN, `/outreach/campaigns/${id}/detail`),
    contacts: path(ROOTS_ADMIN, '/outreach/contacts'),
    contactsImportDeskos: path(ROOTS_ADMIN, '/outreach/contacts/import/deskos'),
    contactsImportCsv: path(ROOTS_ADMIN, '/outreach/contacts/import/csv'),
    templates: path(ROOTS_ADMIN, '/outreach/templates'),
    settings: path(ROOTS_ADMIN, '/outreach/settings'),
    contactAttributes: path(ROOTS_ADMIN, '/outreach/contacts/attributes'),
  },
};

// Brokers an operator has added sign in here to see what is free. Read only.
export const PATH_LIVE_INVENTORY = {
  root: ROOTS_LIVE_INVENTORY,
  login: path(ROOTS_LIVE_INVENTORY, '/login'),
  setPassword: path(ROOTS_LIVE_INVENTORY, '/set-password'),
};

export const PATH_ATLAS = {
  root: ROOTS_ATLAS,
  login: path(ROOTS_ATLAS, '/login'),
  dashboard: path(ROOTS_ATLAS, '/dashboard'),
  inventory: path(ROOTS_ATLAS, '/inventory'),
  spaceDetail: (id) => path(ROOTS_ATLAS, `/inventory/${id}`),
  leads: path(ROOTS_ATLAS, '/leads'),
  leadCreate: path(ROOTS_ATLAS, '/leads/create'),
  leadDetail: (id) => path(ROOTS_ATLAS, `/leads/${id}`),
  leadEdit: (id) => path(ROOTS_ATLAS, `/leads/${id}/edit`),
  proposals: path(ROOTS_ATLAS, '/proposals'),
  createProposal: path(ROOTS_ATLAS, '/proposals/create'),
  proposalDetail: (id) => path(ROOTS_ATLAS, `/proposals/${id}`),
  proposalEdit: (id) => path(ROOTS_ATLAS, `/proposals/${id}/edit`),
  calendar: path(ROOTS_ATLAS, '/calendar'),
};

export const PATH_USER = {
  root: ROOTS_USER,
  login: path(ROOTS_USER, '/login'),
  dashboard: path(ROOTS_USER, '/dashboard'),
  settings: path(ROOTS_USER, '/settings'),
  feed: path(ROOTS_USER, '/feed'),
  feedPost: (id) => path(ROOTS_USER, `/feed/post/${id}`),
  events: path(ROOTS_USER, '/events'),
  eventDetail: (id) => path(ROOTS_USER, `/events/${id}`),
  orders: path(ROOTS_USER, '/my-orders'),
  profile: path(ROOTS_USER, '/profile'),
  buySpace: path(ROOTS_USER, '/plans'),
  meetingRoom: path(ROOTS_USER, '/meeting-room'),
  meetingRoomMyBookings: path(ROOTS_USER, '/meeting-room/my-bookings'),
  meetingRoomDetail: (id) => path(ROOTS_USER, `/meeting-room/room/${id}`),
  meetingRoomExtend: (bookingId) => path(ROOTS_USER, `/meeting-room/extend/${bookingId}`),
  meetingRoomCredits: path(ROOTS_USER, '/plans?type=credit-packs'),
  planStore: path(ROOTS_USER, '/plans'),
  meetingRoomDirectory: (orgSlug) => `/meeting-rooms/${orgSlug}`,
  myVisits: path(ROOTS_USER, '/my-visits'),
  courier: path(ROOTS_USER, '/courier'),
  refreshments: path(ROOTS_USER, '/refreshments'),
  myOrders: path(ROOTS_USER, '/my-orders'),
  serviceRequests: {
    root: path(ROOTS_USER, '/service-requests'),
    list: path(ROOTS_USER, '/service-requests/list'),
  },
  companyManagement: {
    root: path(ROOTS_USER, '/company/home/'),
    attendance: path(ROOTS_USER, '/company/attendance/'),
    // attendanceSettings: path(ROOTS_USER, '/team/attendance/settings/'),
    memberAttendance: (id) => path(ROOTS_USER, `/company/attendance/member/${id}`),
    teamMembers: path(ROOTS_USER, '/company/members/'),
    billing: path(ROOTS_USER, '/company/billing/'),
    settings: path(ROOTS_USER, '/company/settings/'),
  },
  downloads: path(ROOTS_USER, '/downloads/'),
  visitorManagement: path(ROOTS_ADMIN, '/visitor-management'),
  printing: {
    root: path(ROOTS_USER, '/printing'),
  } ,
  mealos: {
    cart: path(ROOTS_USER, '/mealos/cart'),
    cafeteriaList: path(ROOTS_USER, '/mealos/cafeteria-list'),
    orders: path(ROOTS_USER, '/mealos/orders'),
    orderTrack: (orderId) => path(ROOTS_USER, `/mealos/order/track/${orderId}`),
    menu: (cafeteriaId) => path(ROOTS_USER, `/mealos/menu/${cafeteriaId}`),
  },
  virtualOffice: path(ROOTS_USER, '/virtual-office'),
  voRenewal: (id) => path(ROOTS_USER, `/virtual-office/renew/${id}`),
  voDocuments: (id) => path(ROOTS_USER, `/virtual-office/documents/${id}`),
};

export const PATH_KIOSK = {
  root: ROOTS_KIOSK,
  login: path(ROOTS_KIOSK, '/login'),
  home: path(ROOTS_KIOSK, '/home'),
  scanner: path(ROOTS_KIOSK, '/scanner'),
};

export const PATH_TC = {
  dashboard: '/dashboard',
  addRecListing: '/recruiter/listing/add',
  addUniListing: '/university/listing/add',
  recPost: (id) => path(`/recruiter/listing/${id}`),
  uniPost: (id) => path(`/university/listing/${id}`),
  recEdit: (id) => path(`/recruiter/listing/edit/${id}`),
  uniEdit: (id) => path(`/university/listing/edit/${id}`),
  company: (id) => path(`/company/${id}`),
  directory: '/directory',
  directoryTpo: '/directory/company',
  directoryCompany: '/directory/tpo',
  directoryRecruiter: '/directory/recruiter',
  listingUniversity: 'university/listings',
  community: '/community',
  about: {
    tnc: '/about/tnc',
    privacyPolicy: '/about/privacy-policy',
    pricing: '/about/pricing',
    refundPolicy: '/about/refund-policy',
    aboutUs: '/about/about-us',
    contactUs: '/about/contact-us',
    howCredits: '/about/how-credits',
  },
  listing: {
    recruiter: '/recruiter/listings/',
    university: '/university/listings/',
    self: '/listing/self/',
  },
  myContacts: '/my-contacts',
  pending: '/pending',
  userSetting: '/user/settings',
  userOrders: '/user/orders',
  upgrade: '/upgrade',
  payment: (id) => path(`/checkout/payment/?id=${id}`),
  paymentSuccess: '/checkout/success',
};

export const PATH_PAGE = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page403: '/403',
  page404: '/404',
  page500: '/500',
  components: '/components',
};

export const PATH_DASHBOARD = {
  root: ROOTS_DASHBOARD,
  general: {
    crossing: path(ROOTS_DASHBOARD, '/crossing'),
  },
  mail: {
    root: path(ROOTS_DASHBOARD, '/mail'),
    all: path(ROOTS_DASHBOARD, '/mail/all'),
  },
  chat: {
    root: path(ROOTS_DASHBOARD, '/chat'),
    new: path(ROOTS_DASHBOARD, '/chat/new'),
    view: (name) => path(ROOTS_DASHBOARD, `/chat/${name}`),
  },
  calendar: path(ROOTS_DASHBOARD, '/calendar'),
  kanban: path(ROOTS_DASHBOARD, '/kanban'),
  permissionDenied: path(ROOTS_DASHBOARD, '/permission-denied'),
  user: {
    root: path(ROOTS_DASHBOARD, '/user'),
    new: path(ROOTS_DASHBOARD, '/user/new'),
    list: path(ROOTS_DASHBOARD, '/user/list'),
    cards: path(ROOTS_DASHBOARD, '/user/cards'),
    profile: path(ROOTS_DASHBOARD, '/user/profile'),
    account: path(ROOTS_DASHBOARD, '/user/account'),
    edit: (name) => path(ROOTS_DASHBOARD, `/user/${name}/edit`),
    demoEdit: path(ROOTS_DASHBOARD, `/user/reece-chung/edit`),
  },
  eCommerce: {
    root: path(ROOTS_DASHBOARD, '/e-commerce'),
    shop: path(ROOTS_DASHBOARD, '/e-commerce/shop'),
    list: path(ROOTS_DASHBOARD, '/e-commerce/list'),
    checkout: path(ROOTS_DASHBOARD, '/e-commerce/checkout'),
    new: path(ROOTS_DASHBOARD, '/e-commerce/product/new'),
    view: (name) => path(ROOTS_DASHBOARD, `/e-commerce/product/${name}`),
    edit: (name) => path(ROOTS_DASHBOARD, `/e-commerce/product/${name}/edit`),
    demoEdit: path(ROOTS_DASHBOARD, '/e-commerce/product/nike-blazer-low-77-vintage/edit'),
    demoView: path(ROOTS_DASHBOARD, '/e-commerce/product/nike-air-force-1-ndestrukt'),
  },
  invoice: {
    root: path(ROOTS_DASHBOARD, '/invoice'),
    list: path(ROOTS_DASHBOARD, '/invoice/list'),
    new: path(ROOTS_DASHBOARD, '/invoice/new'),
    view: (id) => path(ROOTS_DASHBOARD, `/invoice/${id}`),
    edit: (id) => path(ROOTS_DASHBOARD, `/invoice/${id}/edit`),
    demoEdit: path(ROOTS_DASHBOARD, '/invoice/e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1/edit'),
    demoView: path(ROOTS_DASHBOARD, '/invoice/e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5'),
  },
  blog: {
    root: path(ROOTS_DASHBOARD, '/blog'),
    posts: path(ROOTS_DASHBOARD, '/blog/posts'),
    new: path(ROOTS_DASHBOARD, '/blog/new'),
    view: (title) => path(ROOTS_DASHBOARD, `/blog/post/${title}`),
    demoView: path(ROOTS_DASHBOARD, '/blog/post/apply-these-7-secret-techniques-to-improve-event'),
  },
};

export const PATH_STUDENT = {
  root: ROOTS_STUDENT,
  general: {
    app: path(ROOTS_STUDENT, '/dashboard/app'),
    social: path(ROOTS_STUDENT, '/dashboard/social'),
    onboarding: path(ROOTS_STUDENT, '/dashboard/setup/onboarding'),

    jobs: path(ROOTS_STUDENT, '/dashboard/jobs'),
    jobsApplied: path(ROOTS_STUDENT, '/dashboard/jobs/applied'),
    internships: path(ROOTS_STUDENT, '/dashboard/internships'),
    hiringDrive: path(ROOTS_STUDENT, '/dashboard/hiring-drive'),

    jobView: (id) => path(ROOTS_STUDENT, `/dashboard/jobs/view/${id}`),
    internshipView: (id) => path(ROOTS_STUDENT, `/dashboard/internships/view/${id}`),
    hiringDriveView: (id) => path(ROOTS_STUDENT, `/dashboard/hiring-drive/view/${id}`),

    events: path(ROOTS_STUDENT, '/dashboard/events/view'),
    eventView: (id) => path(ROOTS_STUDENT, `/dashboard/events/view/${id}`),

    opportunities: path(ROOTS_STUDENT, '/dashboard/opportunities/view'),
    opportunityView: (id) => path(ROOTS_STUDENT, `/dashboard/opportunities/view/${id}`),

    feed: path(ROOTS_STUDENT, '/dashboard/feed/view'),
    feedPostView: (id) => path(ROOTS_STUDENT, `/dashboard/feed/view/${id}`),

    workshops: path(ROOTS_STUDENT, '/dashboard/workshops/view'),
    workshopPostView: (id) => path(ROOTS_STUDENT, `/dashboard/workshops/view/${id}`),

    careerTrack: path(ROOTS_STUDENT, '/dashboard/career-track'),
    InterviewQA: path(ROOTS_STUDENT, '/dashboard/career-track/interview-qa'),
  },
  user: {
    // root: path(ROOTS_STUDENT, '/dashboard/user'),
    // new: path(ROOTS_STUDENT, '/dashboard/user/new'),
    // list: path(ROOTS_STUDENT, '/dashboard/user/list'),
    // cards: path(ROOTS_STUDENT, '/dashboard/user/cards'),
    profile: path(ROOTS_STUDENT, '/dashboard/user/profile'),
    editProfile: path(ROOTS_STUDENT, '/dashboard/user/profile/edit'),
    account: path(ROOTS_STUDENT, '/dashboard/user/account'),
    // edit: (name) => path(ROOTS_STUDENT, `/dashboard/user/${name}/edit`),
    // demoEdit: path(ROOTS_STUDENT, `/dashboard/user/reece-chung/edit`),
  },
};

export const PATH_UNIVERSITY = {
  root: ROOTS_UNIVERSITY,
  general: {
    app: path(ROOTS_UNIVERSITY, '/dashboard/app'),
    addStudent: path(ROOTS_UNIVERSITY, '/dashboard/add/student'),
    editStudent: (id) => path(ROOTS_UNIVERSITY, `/dashboard/edit/student/${id}`),
    groups: path(ROOTS_UNIVERSITY, '/dashboard/groups'),
    newGroup: path(ROOTS_UNIVERSITY, '/dashboard/groups/new'),
    studentActivity: path(ROOTS_UNIVERSITY, '/dashboard/student-activity'),
    studentDetails: path(ROOTS_UNIVERSITY, '/dashboard/student-details'),

    jobs: path(ROOTS_UNIVERSITY, '/dashboard/jobs/list'),
    jobsApproval: path(ROOTS_UNIVERSITY, '/dashboard/jobs/approval'),

    hiringDrive: path(ROOTS_UNIVERSITY, '/dashboard/hiring-drive'),
    newHiringDrive: path(ROOTS_UNIVERSITY, '/dashboard/hiring-drive/new'),
    editHiringDrive: (id) => path(ROOTS_UNIVERSITY, `/dashboard/hiring-drive/${id}/edit`),

    rhCompanyList: path(ROOTS_UNIVERSITY, '/dashboard/recruiterhub/company/list'),
    rhMessages: path(ROOTS_UNIVERSITY, '/dashboard/recruiterhub/messages'),

    events: path(ROOTS_UNIVERSITY, '/dashboard/events/view'),
    eventView: (id) => path(ROOTS_UNIVERSITY, `/dashboard/events/view/${id}`),
    eventForm: path(ROOTS_UNIVERSITY, '/dashboard/events/new'),

    feed: path(ROOTS_UNIVERSITY, '/dashboard/feed/view'),
    feedPostView: (id) => path(ROOTS_UNIVERSITY, `/dashboard/feed/view/${id}`),
    feedNewPost: path(ROOTS_UNIVERSITY, '/dashboard/feed/new'),
    feedPostEdit: (id) => path(ROOTS_UNIVERSITY, `/dashboard/feed/edit/${id}`),

    workshops: path(ROOTS_UNIVERSITY, '/dashboard/workshops/view'),
    workshopPostView: (id) => path(ROOTS_UNIVERSITY, `/dashboard/workshops/view/${id}`),
    workshopsNewPost: path(ROOTS_UNIVERSITY, '/dashboard/workshops/new'),
    workshopsEditPost: (id) => path(ROOTS_UNIVERSITY, `/dashboard/workshops/edit/${id}`),
  },
  user: {
    account: path(ROOTS_UNIVERSITY, '/dashboard/user/account'),
  },
};

export const PATH_COMPANY = {
  root: ROOTS_COMPANY,
  general: {
    newJobPosting: path(ROOTS_COMPANY, '/dashboard/jobs/new/'),
    JobsList: path(ROOTS_COMPANY, '/dashboard/jobs/list/'),
    JobView: (name) => path(ROOTS_COMPANY, `/dashboard/jobs/${name}`),
    JobEdit: (name) => path(ROOTS_COMPANY, `/dashboard/jobs/${name}/edit`),
  },
  admin: {
    JobCreate: path(ROOTS_COMPANY, '/dashboard/admin/jobs/new'),
    JobList: path(ROOTS_COMPANY, '/dashboard/admin/jobs/list'),
  },
};

export const PATH_RESTAURANT_ADMIN = {
  root: ROOTS_RESTAURANT_ADMIN,
  dashboard: `${ROOTS_RESTAURANT_ADMIN}/dashboard`,
  menuManagement: `${ROOTS_RESTAURANT_ADMIN}/menu-management`,
  ordersManagement: `${ROOTS_RESTAURANT_ADMIN}/orders-management`,
  kitchenDisplay: `${ROOTS_RESTAURANT_ADMIN}/kitchen-display`,
  inventoryManagement: `${ROOTS_RESTAURANT_ADMIN}/inventory-management`,
  rawInventory: `${ROOTS_RESTAURANT_ADMIN}/raw-inventory`,
  inventory: {
    root: `${ROOTS_RESTAURANT_ADMIN}/raw-inventory`,
    dashboard: `${ROOTS_RESTAURANT_ADMIN}/raw-inventory`,
    stock: `${ROOTS_RESTAURANT_ADMIN}/raw-inventory/stock`,
    purchasing: `${ROOTS_RESTAURANT_ADMIN}/raw-inventory/purchasing`,
    grns: `${ROOTS_RESTAURANT_ADMIN}/raw-inventory/grns`,
    stockTakes: `${ROOTS_RESTAURANT_ADMIN}/raw-inventory/stock-takes`,
    prepared: `${ROOTS_RESTAURANT_ADMIN}/raw-inventory/prepared`,
    vendors: `${ROOTS_RESTAURANT_ADMIN}/raw-inventory/vendors`,
    categories: `${ROOTS_RESTAURANT_ADMIN}/raw-inventory/categories`,
    reports: `${ROOTS_RESTAURANT_ADMIN}/raw-inventory/reports`,
  },
  integrations: `${ROOTS_RESTAURANT_ADMIN}/integration`,
  staffAssignment: `${ROOTS_RESTAURANT_ADMIN}/staff-assignment`,
  cafeSettings: `${ROOTS_RESTAURANT_ADMIN}/settings`,
  pos: `${ROOTS_RESTAURANT_ADMIN}/pos`,
  coupons: `${ROOTS_RESTAURANT_ADMIN}/coupons`,
  mealSchedule: `${ROOTS_RESTAURANT_ADMIN}/meal-schedule`,
  menuImport: `${ROOTS_RESTAURANT_ADMIN}/menu-import`,
  rawMaterialImport: `${ROOTS_RESTAURANT_ADMIN}/raw-material-import`,
  importCenter: `${ROOTS_RESTAURANT_ADMIN}/import`,
};

export const PATH_BOSS = {
  root: '/boss',
  orderList: '/boss/order/list',
  orderDetail: (id) => `/boss/order/detail/${id}`,
  orderCreate: '/boss/order/create',
  organizationList: '/boss/organization/list',
  organizationDetail: (id) => `/boss/organization/detail/${id}`,
  organizationOnboard: '/boss/organization/create',
  managerOrgList: '/boss/manager/organizations',
  managerOrgDetail: (id) => `/boss/manager/organizations/${id}`,
  billing: '/boss/billing',
  emailSettings: '/boss/settings/email',
  whatsappConsole: '/boss/whatsapp',
  printlyAccess: '/boss/printly',
  printlyReleases: '/boss/printly/releases',
  aiCredits: '/boss/ai-credits',
  store: '/boss/store',
  adoption: '/boss/adoption',
  training: {
    requests: '/boss/training/requests',
    sessions: '/boss/training/sessions',
    settings: '/boss/training/settings',
  },
  support: {
    config: '/boss/support/config',
    tickets: '/boss/support/tickets',
    ticketDetail: (id) => `/boss/support/tickets/${id}`,
  },
  crm: {
    myDay: '/boss/crm/my-day',
    dashboard: '/boss/crm/dashboard',
    accounts: '/boss/crm/accounts',
    accountDetail: (id) => `/boss/crm/accounts/${id}`,
    activities: '/boss/crm/activities',
    contacts: '/boss/crm/contacts',
    team: '/boss/crm/team',
    teamMember: (id) => `/boss/crm/team/${id}`,
    connections: '/boss/crm/connections',
  },
  // Sprints, stories and deploys. The whole section a developer or product
  // manager can reach.
  build: {
    myWork: '/boss/build/my-work',
    board: '/boss/build/board',
    backlog: '/boss/build/backlog',
    sprints: '/boss/build/sprints',
    deploys: '/boss/build/deploys',
    deployDetail: (id) => `/boss/build/deploy/${id}`,
    queue: '/boss/build/queue',
    repos: '/boss/build/repos',
    connections: '/boss/build/connections',
  },
  // deskos.net content. The only section a publisher can reach.
  marketing: {
    posts: '/boss/website/posts',
    postCreate: '/boss/website/posts/new',
    postEdit: (id) => `/boss/website/posts/${id}`,
    taxonomy: '/boss/website/taxonomy',
    media: '/boss/website/media',
  },
};

export const PATH_DOCS = 'https://docs-minimals.vercel.app/introduction';
export const PATH_KITCHEN_DISPLAY_SYSTEM = '/chef/kitchen-display';
export const PATH_CHEF_PRINTER_SETTINGS = '/chef/printer-settings';
export const PATH_CHEF_POS = '/chef/pos';
export const PATH_CHEF_ORDERS = '/chef/orders-management';
